import { createSystem, defaultConfig, defineConfig } from '@chakra-ui/react';

/**
 * Chakra v3 design system for the web app.
 *
 * This file is the source of truth for design tokens (colors, fonts,
 * spacing, radii). It replaces the NativeBase `extendTheme` theme that
 * used to live in `src/theme/theme.js`. The tier-named palettes below
 * are preserved from the legacy native-base theme so migrated consumers
 * that reference `primary.500`, `secondary.200`, etc. keep working.
 *
 * Component visual shapes (Button variants, etc.) live with each wrapper
 * in `src/components/ui/`, not here.
 */
const palettes = {
  primary: {
    50: '#DEF2F2',
    100: '#CEF2F1',
    200: '#B7E6E4',
    300: '#ADE0DC',
    400: '#74CCC6',
    500: '#72CCC6',
    600: '#41B4AC',
    700: '#0E7490',
    800: '#2B7873',
    900: '#1B4B48',
  },
  secondary: {
    50: '#74A6AD',
    100: '#3D7F87',
    200: '#2D6670',
    300: '#245B62',
    400: '#17484E',
    500: '#043338',
    600: '#032326',
    700: '#0A1F22',
    800: '#020B0D',
    900: '#010303',
  },
  muted: {
    50: '#FDFEFA',
    100: '#F7F5ED',
    200: '#E0E1E1',
    300: '#D4D4D8',
    400: '#ABAFB1',
    500: '#505455',
    600: '#505455',
    900: '#1f2937',
  },
  light: {
    50: '#FDFEFA',
    100: '#F7F5ED',
  },
  tertiary: { 500: '#F7D432' },
  aqua: { 500: '#5299A9' },
  // "Blue" in the design system. The previous #D2E3FF had no consumers, so it
  // is corrected here rather than carried alongside a second blue.
  // 50 is the fill of an informational note, such as the employer email prompt.
  // 100 is that note's border, and the preferred-name pill.
  // 500 is the design's band ink, the text colour used on a Blue band.
  blue: { 50: '#EBF6FA', 100: '#B8E3FD', 500: '#0E3E46' },
  blueGray: { 100: '#f1f5f9', 600: '#718F9A' },
  // "Cement" in the design system: borders on inputs and unselected options.
  cement: { 200: '#E1E7E7' },
  // Prototype --field-border: the hairline around cards and chips.
  fieldBorder: { 200: '#E3E9EC' },
  cloud: { 100: '#E7E7E7', 200: '#E7E7E7' },
  coolGray: { 100: '#E5E7EB' },
  // 100 is the page background, so panels need 200 to be visible on it.
  feather: { 100: '#EEF4F6', 200: '#E4EEF1' },
  orange: { 400: '#E46E4F', 500: '#F8623F' },
  red: { 100: '#FEE2E2', 200: '#F3B7B7', 500: '#CD3A3A', 700: '#7A2323' },
  steel: { 200: '#BCC7D7' },
  trueGray: { 50: '#FAFAFA' },
};

const colorTokens: Record<string, { value: string }> = Object.fromEntries(
  Object.entries(palettes).flatMap(([family, shades]) =>
    Object.entries(shades).map(([shade, value]) => [
      `${family}.${shade}`,
      { value },
    ])
  )
);

const config = defineConfig({
  // Chakra emits colour variables with the dot escaped
  // (`--chakra-colors-feather\.100`), which is awkward to reference from plain
  // CSS and from gradients. Re-publish the page background under a stable name
  // so `App.css` and the CTA gradients can share one source of truth.
  globalCss: {
    ':root': {
      '--ayble-page-bg': '{colors.feather.100}',
    },
  },
  theme: {
    tokens: {
      colors: colorTokens,
      fonts: {
        heading: { value: '"Recoleta", serif' },
        body: { value: '"Figtree", sans-serif' },
        mono: { value: '"Figtree", monospace' },
      },
      fontSizes: {
        '2xs': { value: '10px' },
        xs: { value: '12px' },
        sm: { value: '14px' },
        md: { value: '15px' },
        lg: { value: '18px' },
        xl: { value: '20px' },
        '2xl': { value: '24px' },
        '3xl': { value: '30px' },
        '4xl': { value: '36px' },
        '5xl': { value: '48px' },
        '6xl': { value: '60px' },
        '7xl': { value: '72px' },
        '8xl': { value: '96px' },
        '9xl': { value: '128px' },
      },
      fontWeights: {
        hairline: { value: '100' },
        thin: { value: '200' },
        light: { value: '300' },
        normal: { value: '400' },
        medium: { value: '500' },
        semibold: { value: '600' },
        bold: { value: '700' },
        extrabold: { value: '800' },
        black: { value: '900' },
        extraBlack: { value: '950' },
      },
      lineHeights: {
        '2xs': { value: '1em' },
        xs: { value: '1.125em' },
        sm: { value: '1.25em' },
        md: { value: '1.275em' },
        lg: { value: '1.25em' },
        xl: { value: '1.1em' },
        '2xl': { value: '1em' },
        '3xl': { value: '2em' },
        '4xl': { value: '3em' },
        '5xl': { value: '4em' },
      },
      // The designs are drawn at two widths: SM_375x813 and 2xl-1536x1092.
      // 375 is a device width, not a breakpoint, so the mobile design maps to
      // the unprefixed base styles. The desktop design maps to `2xl` (1536),
      // which already lines up with the Figma frame width.
      breakpoints: {
        sm: { value: '480px' },
        md: { value: '768px' },
        lg: { value: '992px' },
        xl: { value: '1280px' },
        '2xl': { value: '1536px' },
      },
      // Design system radii. Named rather than mapped onto Chakra's md/lg/full
      // so overriding them cannot change unrelated components.
      radii: {
        option: { value: '8px' },
        row: { value: '12px' },
        pill: { value: '999px' },
        panel: { value: '18px' },
        sheet: { value: '32px' },
      },
      // Content widths from the design (form column, prose column, page).
      sizes: {
        formWidth: { value: '480px' },
        ctaWidth: { value: '420px' },
        proseWidth: { value: '580px' },
        pageWidth: { value: '1040px' },
      },
      // NB used unitless borderWidth="1" / {1} meaning 1px. Chakra v3
      // resolves borderWidth against `borderWidths` tokens — without
      // these, string "1" is emitted as invalid CSS `border-width: 1`
      // and the border disappears.
      borderWidths: {
        '0': { value: '0px' },
        '1': { value: '1px' },
        '2': { value: '2px' },
        '4': { value: '4px' },
        '8': { value: '8px' },
      },
      // Numeric shadows match NativeBase's elevation scale (used as
      // `shadow="9"` on ReviewCard / VisitCard, `shadow="1"` on
      // CardInputButton). Converted from NB's RN shadow props to
      // CSS box-shadow the same way react-native-web did.
      shadows: {
        '0': { value: '0px 1px 1px 0px rgba(0, 0, 0, 0.18)' },
        '1': { value: '0px 1px 1.41px 0px rgba(0, 0, 0, 0.2)' },
        '2': { value: '0px 1px 2.22px 0px rgba(0, 0, 0, 0.22)' },
        '3': { value: '0px 2px 2.62px 0px rgba(0, 0, 0, 0.23)' },
        '4': { value: '0px 2px 3.84px 0px rgba(0, 0, 0, 0.25)' },
        '5': { value: '0px 3px 4.65px 0px rgba(0, 0, 0, 0.27)' },
        '6': { value: '0px 3px 4.65px 0px rgba(0, 0, 0, 0.29)' },
        '7': { value: '0px 4px 4.65px 0px rgba(0, 0, 0, 0.3)' },
        '8': { value: '0px 4px 5.46px 0px rgba(0, 0, 0, 0.32)' },
        '9': { value: '0px 5px 6.27px 0px rgba(0, 0, 0, 0.34)' },
      },
    },
    // Named text styles from the design system. These are kept separate from
    // the numeric `fontSizes` scale on purpose: the design uses 12/14/16/28px
    // while the legacy scale has no 16 or 28 (md is 15, lg is 18), and
    // renaming the scale would change every existing consumer.
    textStyles: {
      'heading.h2': {
        value: {
          fontFamily: 'heading',
          // The prototype steps the screen headline up at its 860px
          // breakpoint; `md` (768px) is the nearest step on our scale.
          fontSize: { base: '28px', md: '36px' },
          fontWeight: '400',
          lineHeight: '1.2',
          letterSpacing: '-0.02em',
        },
      },
      'label.section': {
        value: {
          fontFamily: 'body',
          fontSize: '13.5px',
          fontWeight: '700',
        },
      },
      'label.hint': {
        value: {
          fontFamily: 'body',
          fontSize: '12px',
          fontWeight: '400',
        },
      },
      'body.check': {
        value: {
          fontFamily: 'body',
          fontSize: '14px',
          fontWeight: '400',
          lineHeight: '1.4',
          letterSpacing: '0.01em',
        },
      },
      'body.sub': {
        value: {
          fontFamily: 'body',
          fontSize: '15px',
          lineHeight: '1.5',
        },
      },
      'body.lg': {
        value: {
          fontFamily: 'body',
          fontSize: '16px',
          fontWeight: '400',
          lineHeight: '1.4',
        },
      },
      'body.lgSemibold': {
        value: {
          fontFamily: 'body',
          fontSize: '16px',
          fontWeight: '600',
          lineHeight: '1',
        },
      },
      'body.md': {
        value: {
          fontFamily: 'body',
          fontSize: '14px',
          fontWeight: '400',
          lineHeight: '1.25',
          letterSpacing: '0.01em',
        },
      },
      'body.mdSemibold': {
        value: {
          fontFamily: 'body',
          fontSize: '14px',
          fontWeight: '600',
          lineHeight: '1.25',
          letterSpacing: '0.02em',
        },
      },
      'body.xs': {
        value: {
          fontFamily: 'body',
          fontSize: '12px',
          fontWeight: '400',
          lineHeight: '1.25',
          letterSpacing: '0.01em',
        },
      },
    },
    // Semantic tokens map our brand color scales to the slots Chakra v3's
    // `colorPalette` system reads. Without these, `colorPalette="primary"`
    // falls through to Chakra's gray defaults.
    semanticTokens: {
      colors: {
        primary: {
          solid: { value: '{colors.primary.500}' },
          contrast: { value: 'white' },
          fg: { value: '{colors.primary.700}' },
          muted: { value: '{colors.primary.100}' },
          subtle: { value: '{colors.primary.50}' },
          emphasized: { value: '{colors.primary.600}' },
          focusRing: { value: '{colors.primary.500}' },
        },
        secondary: {
          solid: { value: '{colors.secondary.500}' },
          contrast: { value: 'white' },
          fg: { value: '{colors.secondary.600}' },
          muted: { value: '{colors.secondary.100}' },
          subtle: { value: '{colors.secondary.50}' },
          emphasized: { value: '{colors.secondary.600}' },
          focusRing: { value: '{colors.secondary.500}' },
        },
      },
    },
  },
});

export const designSystem = createSystem(defaultConfig, config);
